import React, { useEffect, useState } from "react";
import { Workspace } from "polotno/canvas/workspace";
import { storeToVideo } from '@polotno/video-export';
import Konva from "konva";

function waitForWorkspaceFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(resolve, 120);
      });
    });
  });
}

async function warmMountedPages(store) {
  const pageIds = (store?.pages || []).map((page) => page.id).filter(Boolean);
  store.pages?.forEach((page) => page.set?.({ _forceMount: true, _rendering: true }));
  await waitForWorkspaceFrame();
  await waitForWorkspaceFrame();

  for (const pageId of pageIds) {
    store.selectPage(pageId);
    await store.waitLoading?.();
    await waitForWorkspaceFrame();
  }
  if (pageIds[0]) {
    store.selectPage(pageIds[0]);
    await store.waitLoading?.();
    await waitForWorkspaceFrame();
  }
}

function getMountedStagePageIds() {
  return Konva.stages.map((stage) => stage.getAttr("pageId")).filter(Boolean);
}

async function exportStoreToVideoWhenWorkspaceIsReady({ store, fps, pixelRatio, onProgress }) {
  try {
    await warmMountedPages(store);
    console.info("[VideoViewer] Starting Polotno video export", {
      pageIds: store.pages?.map((page) => page.id),
      mountedStagePageIds: getMountedStagePageIds(),
      width: store.width,
      height: store.height,
      duration: store.duration,
    });
    return await storeToVideo({ store, fps, pixelRatio, onProgress });
  } finally {
    store.pages?.forEach((page) => page.set?.({ _forceMount: false, _rendering: false }));
  }
}

export function VideoViewer({ store, templateId, eventId, mode }) {
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    if (!store) return;

    setLoading(true);

    const timeout = setTimeout(async () => {
      try {
        // 🌀 Step 1: Export video from Polotno
        // Adjust FPS, duration, etc. as needed
        // const dataUrl = await store.toVideoDataURL({
        //   fps: 30,          // smooth animation
        //   quality: 1,       // highest quality
        //   duration: 5000,   // 5 seconds total
        //   format: "mp4",    // explicitly set to mp4 if supported
        // });

        // // 🧱 Step 2: Convert DataURL → Blob
        // const res = await fetch(dataUrl);
        // const blob = await res.blob();

        const videoBlob = await exportStoreToVideoWhenWorkspaceIsReady({
          store,
          fps: 30, // Frames per second (default: 30)
          pixelRatio: 2, // Pixel ratio for quality (default: 1)
          onProgress: (progress, frameTime) => {
            console.log(`Progress: ${Math.round(progress * 100)}%`);
            console.log(`Frame render time: ${frameTime}ms`);
          },
        });

        // 📦 Step 3: Build FormData for upload
        const formData = new FormData();
        formData.append("file", videoBlob, "animation.mp4");
        formData.append("id", mode === "template" ? templateId : eventId);
        formData.append("updateType", mode);
        formData.append("fileType", "video");

        const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000") + "/media/polotno_upload_gif_video";

        console.log("Uploading to:", API_URL);

        // 🚀 Step 4: Upload to FastAPI
        const res1 = await fetch(API_URL, { method: "POST", body: formData });
        if (!res1.ok) throw new Error(`Upload failed: ${res1.statusText}`);
        const data = await res1.json();

        // 🎥 Step 5: Display uploaded video
        setVideoUrl(data.file_url || data.video_url || data.media_url);
      } catch (err) {
        console.error("Video upload error:", err);
      } finally {
        setLoading(false);
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [eventId, mode, store, templateId]);

  if (!store) return <div>No store found (try navigating again)</div>;

  const exportWorkspace = (
    <div className="video-export-workspace" aria-hidden="true">
      <Workspace
        store={store}
        pageControlsEnabled={false}
        renderOnlyActivePage={false}
      />
    </div>
  );

  if (loading) {
    return (
      <>
        {exportWorkspace}
        <div className="flex flex-col items-center justify-center h-full">
          <div className="w-48 h-48 bg-gray-200 animate-pulse rounded-lg"></div>
          <p className="mt-4 text-gray-500 italic">Rendering Video preview...</p>
        </div>
      </>
    );
  }

  if (videoUrl) {
    console.log("videoUrl:", videoUrl);
  }

  return (
    <>
      {exportWorkspace}
      <div className="generated-video-shell">
        <video
          src={videoUrl}
          controls
          loop
          className="generated-video-player"
        />
      </div>
    </>
  );
}
